import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Sparkles, Zap, Wrench, Network, Bot } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ChatbotWidget from "../components/ChatbotWidget";
import { api } from "../lib/api";

export default function Pricing() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", website: "",
    project_type: "", budget: "", ai_search_interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Please fill in your name and email."); return; }
    setSubmitting(true);
    try {
      await api.post("/leads", form);
      setDone(true);
      toast.success("Got it — we'll send your custom quote within one business day.");
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="bg-[#FDFBF7] min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-[480px] h-[480px] rounded-full -z-10 opacity-50"
             style={{ background: "radial-gradient(closest-side, rgba(197,160,89,0.32), transparent 70%)" }}/>
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-10 md:pb-12">
          <p className="label-eyebrow">Get a custom quote</p>
          <h1 className="font-display text-5xl md:text-6xl tracking-tight mt-4 font-medium leading-[1.05] max-w-4xl">
            Every business is different — so is <em className="text-[#C5A059] not-italic">every quote</em>.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-[#5C5C5C] max-w-3xl leading-relaxed">
            Tell us about your business and we'll send a custom quote within one business day. No pressure, no fixed packages — just a clear scope and price built around your goals.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-5xl">
            {[
              { icon: Sparkles, label: "AI-designed",       sub: "Premium look, half the cost" },
              { icon: Zap,      label: "Under 1 week",       sub: "From kickoff to launch" },
              { icon: Bot,      label: "AI-search optimized",sub: "Google + ChatGPT + Claude" },
              { icon: Network,  label: "Fully integrated",   sub: "CRM · email · payments" },
            ].map((v) => {
              const Icon = v.icon;
              return (
                <div key={v.label} className="flex items-center gap-3 rounded-xl border border-[#EBE8DF] bg-white px-4 py-3">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#FDFBF7] border border-[#EBE8DF] text-[#C5A059] shrink-0">
                    <Icon size={15} strokeWidth={1.8}/>
                  </span>
                  <div className="leading-tight">
                    <p className="text-[14px] font-medium">{v.label}</p>
                    <p className="text-[12px] text-[#8B8C89]">{v.sub}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* QUOTE FORM */}
      <section className="pb-20">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          {done ? (
            <div data-testid="quote-success" className="border border-[#EBE8DF] bg-white p-10 rounded-2xl">
              <p className="label-eyebrow">Quote requested</p>
              <h3 className="font-display text-3xl md:text-4xl mt-3 leading-tight">Thanks, {form.name.split(" ")[0]}.</h3>
              <p className="mt-4 text-[#5C5C5C] leading-relaxed text-lg max-w-2xl">
                We'll review your business, prep a custom proposal, and reply within one business day from <span className="text-[#1A1A1A]">hello@shumwaytech.com</span>.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/" className="btn-secondary">Back to home</Link>
                <Link to="/ai-search" className="btn-primary">Read: Future of AI Search <ArrowRight size={16}/></Link>
              </div>
            </div>
          ) : (
          <form onSubmit={submit} data-testid="quote-form" className="border border-[#EBE8DF] bg-white p-8 md:p-12 rounded-2xl grid md:grid-cols-2 gap-x-10 gap-y-2">
            <div className="md:col-span-2 mb-4">
              <p className="label-eyebrow">Request your custom quote</p>
              <h3 className="font-display text-3xl mt-2 leading-tight">Tell us about your business.</h3>
            </div>

            <div><label className="label-eyebrow">Name *</label>
              <input data-testid="q-name" className="input-line" value={form.name} onChange={set("name")} placeholder="Jane Doe" required/></div>
            <div><label className="label-eyebrow">Email *</label>
              <input data-testid="q-email" type="email" className="input-line" value={form.email} onChange={set("email")} placeholder="jane@company.com" required/></div>

            <div><label className="label-eyebrow">Phone (optional)</label>
              <input data-testid="q-phone" className="input-line" value={form.phone} onChange={set("phone")} placeholder="(555) 010-0123"/></div>
            <div><label className="label-eyebrow">Business name</label>
              <input data-testid="q-company" className="input-line" value={form.company} onChange={set("company")} placeholder="Acme Inc."/></div>

            <div><label className="label-eyebrow">Current website (if any)</label>
              <input data-testid="q-website" className="input-line" value={form.website} onChange={set("website")} placeholder="https://"/></div>
            <div>
              <label className="label-eyebrow">Top goal</label>
              <select data-testid="q-goal" className="input-line" value={form.project_type} onChange={set("project_type")}>
                <option value="">Select…</option>
                <option>More phone calls</option>
                <option>More form submissions</option>
                <option>More appointments / bookings</option>
                <option>Better SEO + AI search visibility</option>
                <option>New website from scratch</option>
                <option>Other</option>
              </select>
            </div>

            <div className="md:col-span-2 mt-4 mb-1">
              <p className="label-eyebrow">Interested in optimizing for AI search?</p>
              <p className="text-[13px] text-[#5C5C5C] mt-1">ChatGPT, Claude, Gemini & Grok answer billions of questions a month — we'll set you up to be named in your category.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {["Yes — that's the main reason I'm here", "Yes, alongside Google SEO", "Maybe — tell me more", "Not right now"].map((opt) => (
                  <button type="button" key={opt}
                    data-testid={`q-ai-${opt.split(" ")[0].toLowerCase()}`}
                    onClick={() => setForm({ ...form, ai_search_interest: opt })}
                    className={`px-4 py-2 rounded-full text-[13px] border transition-colors ${
                      form.ai_search_interest === opt
                        ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                        : "bg-white text-[#1A1A1A] border-[#EBE8DF] hover:border-[#C5A059]"
                    }`}>
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            <div className="md:col-span-2 mt-2">
              <label className="label-eyebrow">Tell us about your business</label>
              <textarea data-testid="q-message" className="input-line" rows={4} value={form.message} onChange={set("message")} placeholder="What you do, who you serve, what's working, what's stuck…"/>
            </div>

            <div className="md:col-span-2 mt-6 flex flex-wrap items-center gap-4">
              <button type="submit" data-testid="q-submit" disabled={submitting} className="btn-primary">
                {submitting ? "Sending…" : "Request my custom quote"} <ArrowRight size={16}/>
              </button>
              <p className="text-[13px] text-[#8B8C89]">Reply within one business day · No obligation</p>
            </div>
          </form>
          )}
        </div>
      </section>

      {/* AI ADVANTAGE */}
      <section className="py-20 md:py-24 bg-[#F5F3ED]/60 border-y border-[#EBE8DF]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <p className="label-eyebrow">The AI advantage</p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight mt-4 font-medium leading-tight">
              Agency quality. Small-business <em className="text-[#C5A059] not-italic">prices</em>.
            </h2>
            <p className="mt-6 text-lg text-[#5C5C5C] leading-relaxed">
              Traditional agencies bill by the hour. We use AI in every step — design, copy, build, QA, SEO research — and pass the savings to you. Same outcomes. Lower price. Faster delivery.
            </p>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-2 gap-3">
            {[
              { traditional: "$8k – $25k",     us: "Small-business friendly", label: "Custom website cost" },
              { traditional: "2 – 4 months",   us: "Under 1 week",            label: "Time to launch" },
              { traditional: "Manual SEO only",us: "Google + AI search",      label: "Search optimization" },
              { traditional: "Add-on pricing", us: "Integrations included",   label: "CRM · email · payments" },
            ].map((row) => (
              <div key={row.label} className="rounded-2xl border border-[#EBE8DF] bg-white p-5">
                <p className="label-eyebrow">{row.label}</p>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-[#8B8C89]">Traditional agency</p>
                    <p className="font-display text-xl mt-1 line-through decoration-[#D1CDC0] text-[#8B8C89]">{row.traditional}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-[#C5A059]">Shumway Tech</p>
                    <p className="font-display text-xl mt-1 text-[#1A1A1A]">{row.us}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OBJECTION */}
      <section className="py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="rounded-2xl border border-[#EBE8DF] bg-white p-10 md:p-14 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-[280px] h-[280px] rounded-full opacity-25"
                 style={{ background: "radial-gradient(closest-side, rgba(197,160,89,0.6), transparent 70%)" }}/>
            <p className="label-eyebrow relative">Honest answer</p>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 font-medium leading-tight relative">
              "Could I just build it myself with AI?"
            </h2>
            <p className="mt-6 text-lg text-[#5C5C5C] leading-relaxed relative max-w-3xl">
              Yes — of course you could try. Or you could hire experts who've shipped hundreds of websites using AI, know exactly how to get the best results, where to host it, and how to plug it into the rest of your technology.
            </p>

            <div className="mt-10 grid md:grid-cols-2 gap-4 relative">
              <div className="p-6 rounded-xl border border-[#EBE8DF]">
                <p className="label-eyebrow">DIY with AI</p>
                <ul className="mt-4 space-y-2.5 text-[15px] text-[#5C5C5C]">
                  {["Weeks of trial-and-error prompting","Generic templates everyone else has","No idea how to wire CRM / email / payments","AI-search optimization? What's that?","Hosting headaches and broken integrations","Time pulled from running your business"].map((x) => (
                    <li key={x} className="flex items-start gap-2"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#D1CDC0] shrink-0"/><span>{x}</span></li>
                  ))}
                </ul>
              </div>
              <div className="p-6 rounded-xl border-2 border-[#C5A059] bg-[#FDFBF7]">
                <p className="label-eyebrow" style={{color:"#C5A059"}}>Hire Shumway Tech</p>
                <ul className="mt-4 space-y-2.5 text-[15px]">
                  {["Hundreds of AI-built sites shipped","Custom design — never a template","CRM, email, and payments wired right","Optimized for Google + ChatGPT + Claude","We pick the hosting and own the setup","You keep running your business"].map((x) => (
                    <li key={x} className="flex items-start gap-3"><Check size={16} className="text-[#C5A059] mt-1 shrink-0"/><span>{x}</span></li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <ChatbotWidget />
    </div>
  );
}
